import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './AuditLog.css';

type ActionType = 'approve' | 'submit' | 'execute' | 'configure' | 'deploy' | 'revoke' | 'reject';
type Outcome = 'success' | 'failure' | 'pending';
type Environment = 'alpha-devnet' | 'beta-testnet' | 'mainnet';

interface AuditEntry {
  id: string;
  timestamp: string;
  actor: string;
  sparkId: string;
  action: ActionType;
  resource: string;
  resourceId: string;
  environment: Environment;
  circuitRef?: string;
  outcome: Outcome;
  details?: string;
}

interface DateRange {
  start: string;
  end: string;
}

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<ActionType | 'all'>('all');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [envFilter, setEnvFilter] = useState<Environment | 'all'>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    start: '',
    end: '',
  });
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setEntries(getMockAuditLog());
      setLoading(false);
    }, 500);
  }, []);

  // Get unique values for filters
  const actors = useMemo(() => [...new Set(entries.map(e => e.actor))], [entries]);
  const actions = useMemo(() => [...new Set(entries.map(e => e.action))], [entries]);

  // Apply filters
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // Search query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches = 
          entry.actor.toLowerCase().includes(q) ||
          entry.resourceId.toLowerCase().includes(q) ||
          entry.details?.toLowerCase().includes(q) ||
          entry.sparkId.toLowerCase().includes(q);
        if (!matches) return false;
      }
      
      // Action filter
      if (actionFilter !== 'all' && entry.action !== actionFilter) return false;
      
      // Actor filter
      if (actorFilter !== 'all' && entry.actor !== actorFilter) return false;
      
      // Environment filter
      if (envFilter !== 'all' && entry.environment !== envFilter) return false;
      
      // Date range
      if (dateRange.start) {
        const entryDate = new Date(entry.timestamp);
        const startDate = new Date(dateRange.start);
        if (entryDate < startDate) return false;
      }
      if (dateRange.end) {
        const entryDate = new Date(entry.timestamp);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59);
        if (entryDate > endDate) return false;
      }
      
      return true;
    });
  }, [entries, searchQuery, actionFilter, actorFilter, envFilter, dateRange]);

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Actor', 'Spark ID', 'Action', 'Resource', 'Resource ID', 'Environment', 'Circuit', 'Outcome', 'Details'];
    const rows = filteredEntries.map(e => [
      e.timestamp,
      e.actor,
      e.sparkId,
      e.action,
      e.resource,
      e.resourceId,
      e.environment,
      e.circuitRef || '',
      e.outcome,
      e.details || '',
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    downloadFile(csv, 'audit-log.csv', 'text/csv');
    setShowExportMenu(false);
  };

  const exportToJSON = () => {
    const json = JSON.stringify(filteredEntries, null, 2);
    downloadFile(json, 'audit-log.json', 'application/json');
    setShowExportMenu(false);
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActionFilter('all');
    setActorFilter('all');
    setEnvFilter('all');
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchQuery || actionFilter !== 'all' || actorFilter !== 'all' || envFilter !== 'all' || dateRange.start || dateRange.end;

  if (loading) {
    return <div className="audit-page"><div className="loading">Loading audit log...</div></div>;
  }

  return (
    <div className="audit-page">
      <div className="page-header">
        <div>
          <h1>Audit Log</h1>
          <p className="subtitle">Complete history of governance actions with compliance export</p>
        </div>
        <div className="export-wrapper">
          <button 
            className="btn-export" 
            onClick={() => setShowExportMenu(!showExportMenu)}
          >
            Export ▾
          </button>
          {showExportMenu && (
            <div className="export-menu">
              <button onClick={exportToCSV}>
                <span className="icon">📊</span>
                Export as CSV
              </button>
              <button onClick={exportToJSON}>
                <span className="icon">{ }</span>
                Export as JSON
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by actor, resource ID, details..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>Action</label>
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value as ActionType | 'all')}>
              <option value="all">All Actions</option>
              {actions.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Actor</label>
            <select value={actorFilter} onChange={e => setActorFilter(e.target.value)}>
              <option value="all">All Actors</option>
              {actors.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Environment</label>
            <select value={envFilter} onChange={e => setEnvFilter(e.target.value as Environment | 'all')}>
              <option value="all">All Environments</option>
              <option value="alpha-devnet">alpha-devnet</option>
              <option value="beta-testnet">beta-testnet</option>
              <option value="mainnet">mainnet</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>From</label>
            <input 
              type="date" 
              value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            />
          </div>
          
          <div className="filter-group">
            <label>To</label>
            <input 
              type="date" 
              value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            />
          </div>
          
          {hasActiveFilters && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="results-count">
          Showing {filteredEntries.length} of {entries.length} entries
        </div>
      </div>

      {/* Audit Table */}
      <div className="table-container">
        <table className="audit-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Environment</th>
              <th>Circuit</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(entry => (
              <tr key={entry.id} className={`outcome-row-${entry.outcome}`}>
                <td className="timestamp">
                  <span className="date">{new Date(entry.timestamp).toLocaleDateString()}</span>
                  <span className="time">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                </td>
                <td className="actor-cell">
                  <span className="actor-name">{entry.actor}</span>
                  <span className="spark-id" title={entry.sparkId}>
                    {entry.sparkId.slice(0, 8)}...
                  </span>
                </td>
                <td>
                  <span className={`action-badge action-${entry.action}`}>
                    {entry.action}
                  </span>
                </td>
                <td className="resource-cell">
                  <span className="resource-type">{entry.resource}</span>
                  <code className="resource-id">{entry.resourceId}</code>
                  {entry.details && (
                    <span className="resource-details">{entry.details}</span>
                  )}
                </td>
                <td>
                  <span className={`env-badge env-${entry.environment}`}>
                    {entry.environment}
                  </span>
                </td>
                <td>
                  {entry.circuitRef ? (
                    <Link to={`/circuits/${entry.circuitRef}`} className="circuit-link">
                      {entry.circuitRef}
                    </Link>
                  ) : (
                    <span className="no-circuit">-</span>
                  )}
                </td>
                <td>
                  <span className={`outcome-badge outcome-${entry.outcome}`}>
                    {entry.outcome === 'success' && '✓'}
                    {entry.outcome === 'failure' && '✗'}
                    {entry.outcome === 'pending' && '⏳'}
                    {entry.outcome}
                  </span>
                </td>
              </tr>
            ))}
            {filteredEntries.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-state">
                  <span className="empty-icon">📋</span>
                  <span>No audit entries match your filters</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getMockAuditLog(): AuditEntry[] {
  return [
    {
      id: 'audit-001',
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'approve',
      resource: 'circuit',
      resourceId: 'circ-deploy-001',
      environment: 'alpha-devnet',
      circuitRef: 'cir-001',
      outcome: 'success',
      details: 'ML-DSA-87 signature verified',
    },
    {
      id: 'audit-002',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'submit',
      resource: 'circuit',
      resourceId: 'circ-deploy-001',
      environment: 'alpha-devnet',
      circuitRef: 'cir-001',
      outcome: 'success',
      details: 'estream.ops.deploy.node.v1',
    },
    {
      id: 'audit-003',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      actor: 'system',
      sparkId: 'sprk_sys_0000000000000000000000000000',
      action: 'execute',
      resource: 'circuit',
      resourceId: 'circ-infra-vpc',
      environment: 'alpha-devnet',
      circuitRef: 'cir-002',
      outcome: 'success',
      details: 'VPC created: estream-alpha',
    },
    {
      id: 'audit-004',
      timestamp: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'approve',
      resource: 'circuit',
      resourceId: 'circ-infra-vpc',
      environment: 'alpha-devnet',
      circuitRef: 'cir-002',
      outcome: 'success',
    },
    {
      id: 'audit-005',
      timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'configure',
      resource: 'lattice',
      resourceId: 'lat-alpha-001',
      environment: 'alpha-devnet',
      outcome: 'success',
      details: 'nodeCount: 1 → 3',
    },
    {
      id: 'audit-006',
      timestamp: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'deploy',
      resource: 'release',
      resourceId: 'console-v0.5.0',
      environment: 'alpha-devnet',
      outcome: 'success',
      details: 'Deployed to alpha-devnet',
    },
    {
      id: 'audit-007',
      timestamp: new Date(Date.now() - 96 * 60 * 60 * 1000).toISOString(),
      actor: 'alice@estream.dev',
      sparkId: 'sprk_1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e',
      action: 'reject',
      resource: 'circuit',
      resourceId: 'circ-risky-001',
      environment: 'beta-testnet',
      circuitRef: 'cir-003',
      outcome: 'failure',
      details: 'Insufficient witness attestations',
    },
    {
      id: 'audit-008',
      timestamp: new Date(Date.now() - 120 * 60 * 60 * 1000).toISOString(),
      actor: 'bob@estream.dev',
      sparkId: 'sprk_2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f',
      action: 'approve',
      resource: 'governance',
      resourceId: 'gov-upgrade-001',
      environment: 'mainnet',
      outcome: 'pending',
      details: 'Awaiting 2nd signature (2/3 required)',
    },
    {
      id: 'audit-009',
      timestamp: new Date(Date.now() - 144 * 60 * 60 * 1000).toISOString(),
      actor: 'todd@estream.dev',
      sparkId: 'sprk_8a7f2c3d4e5f6a7b8c9d0e1f2a3b4c5d',
      action: 'revoke',
      resource: 'key',
      resourceId: 'key-old-001',
      environment: 'alpha-devnet',
      outcome: 'success',
      details: 'Key rotation completed',
    },
    {
      id: 'audit-010',
      timestamp: new Date(Date.now() - 168 * 60 * 60 * 1000).toISOString(),
      actor: 'system',
      sparkId: 'sprk_sys_0000000000000000000000000000',
      action: 'execute',
      resource: 'circuit',
      resourceId: 'circ-monitor-001',
      environment: 'mainnet',
      circuitRef: 'cir-004',
      outcome: 'success',
      details: 'Health check passed',
    },
  ];
}
